import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { LlmModel, LlmProvider, ReasoningEffort } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateLlmModel,
  useUpdateLlmModel,
} from "@/features/llms/hooks/useLlmModels";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  model?: LlmModel | null;
}

const PROVIDERS: { value: LlmProvider; label: string }[] = [
  { value: "anthropic", label: "Anthropic" },
  { value: "openai", label: "OpenAI" },
  { value: "google", label: "Google" },
  { value: "custom", label: "Custom" },
];

export function LlmFormDialog({ open, onOpenChange, model }: Props) {
  const { t } = useTranslation("common");
  const isEdit = Boolean(model);
  const create = useCreateLlmModel();
  const update = useUpdateLlmModel(model?.id ?? "");

  const [provider, setProvider] = useState<LlmProvider>("openai");
  const [modelId, setModelId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [supportsReasoning, setSupportsReasoning] = useState(false);
  const [supportsMultimodal, setSupportsMultimodal] = useState(false);
  const [supportsWebSearch, setSupportsWebSearch] = useState(false);
  // null = "sem default" (modelo nao usa reasoning a nao ser por
  // override do agente).
  const [defaultEffort, setDefaultEffort] = useState<ReasoningEffort | null>(
    null,
  );
  // String para permitir vazio = null (usa o default global do runtime).
  const [maxOutputTokens, setMaxOutputTokens] = useState("");

  useEffect(() => {
    if (open && model) {
      setProvider(model.provider);
      setModelId(model.model_id);
      setDisplayName(model.display_name);
      setDescription(model.description ?? "");
      setSupportsReasoning(model.supports_reasoning);
      setSupportsMultimodal(model.supports_multimodal);
      setSupportsWebSearch(model.supports_web_search);
      setDefaultEffort(model.default_reasoning_effort);
      setMaxOutputTokens(model.max_output_tokens?.toString() ?? "");
    } else if (open) {
      setProvider("openai");
      setModelId("");
      setDisplayName("");
      setDescription("");
      setSupportsReasoning(false);
      setSupportsMultimodal(false);
      setSupportsWebSearch(false);
      setDefaultEffort(null);
      setMaxOutputTokens("");
    }
  }, [open, model]);

  const maxTokensValue: number | null = maxOutputTokens.trim()
    ? Number(maxOutputTokens)
    : null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (isEdit && model) {
        await update.mutateAsync({
          display_name: displayName,
          description: description || undefined,
          supports_reasoning: supportsReasoning,
          supports_multimodal: supportsMultimodal,
          supports_web_search: supportsWebSearch,
          default_reasoning_effort: supportsReasoning ? defaultEffort : null,
          max_output_tokens: maxTokensValue,
        });
        toast.success(t("pages.llmModels.toasts.updated"));
      } else {
        await create.mutateAsync({
          provider,
          model_id: modelId,
          display_name: displayName,
          description: description || undefined,
          supports_reasoning: supportsReasoning,
          supports_multimodal: supportsMultimodal,
          supports_web_search: supportsWebSearch,
          default_reasoning_effort: supportsReasoning ? defaultEffort : null,
          max_output_tokens: maxTokensValue,
        });
        toast.success(t("pages.llmModels.toasts.created"));
      }
      onOpenChange(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  const pending = create.isPending || update.isPending;
  const disabled =
    pending ||
    displayName.length < 1 ||
    (!isEdit && modelId.length < 1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t("pages.llmModels.form.titleEdit")
              : t("pages.llmModels.form.titleNew")}
          </DialogTitle>
          <DialogDescription>
            {t("pages.llmModels.form.description")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isEdit ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="llm-provider">
                  {t("pages.llmModels.form.provider")}
                </Label>
                <Select
                  value={provider}
                  onValueChange={(v) => setProvider(v as LlmProvider)}
                >
                  <SelectTrigger id="llm-provider">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVIDERS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="llm-model-id">
                  {t("pages.llmModels.form.modelId")}
                </Label>
                <Input
                  id="llm-model-id"
                  required
                  value={modelId}
                  onChange={(e) => setModelId(e.target.value)}
                  placeholder={t("pages.llmModels.form.modelIdPlaceholder")}
                />
                <p className="text-xs text-muted-foreground">
                  {t("pages.llmModels.form.modelIdHint")}
                </p>
              </div>
            </>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="llm-display-name">
              {t("pages.llmModels.form.displayName")}
            </Label>
            <Input
              id="llm-display-name"
              required
              minLength={1}
              maxLength={255}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="llm-desc">
              {t("pages.llmModels.form.descriptionField")}
            </Label>
            <Textarea
              id="llm-desc"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="llm-max-output-tokens">
              {t("pages.llmModels.form.maxOutputTokens")}
            </Label>
            <Input
              id="llm-max-output-tokens"
              type="number"
              min={256}
              max={200000}
              value={maxOutputTokens}
              onChange={(e) => setMaxOutputTokens(e.target.value)}
              placeholder={t(
                "pages.llmModels.form.maxOutputTokensPlaceholder",
              )}
            />
            <p className="text-xs text-muted-foreground">
              {t("pages.llmModels.form.maxOutputTokensHint")}
            </p>
          </div>
          <div className="space-y-2 rounded-md border p-3">
            <div className="flex items-center gap-2">
              <input
                id="llm-supports-multimodal"
                type="checkbox"
                checked={supportsMultimodal}
                onChange={(e) => setSupportsMultimodal(e.target.checked)}
              />
              <Label htmlFor="llm-supports-multimodal">
                {t("pages.llmModels.form.supportsMultimodal")}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("pages.llmModels.form.supportsMultimodalHint")}
            </p>
          </div>
          <div className="space-y-2 rounded-md border p-3">
            <div className="flex items-center gap-2">
              <input
                id="llm-supports-web-search"
                type="checkbox"
                checked={supportsWebSearch}
                onChange={(e) => setSupportsWebSearch(e.target.checked)}
              />
              <Label htmlFor="llm-supports-web-search">
                {t("pages.llmModels.form.supportsWebSearch")}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("pages.llmModels.form.supportsWebSearchHint")}
            </p>
          </div>
          <div className="space-y-2 rounded-md border p-3">
            <div className="flex items-center gap-2">
              <input
                id="llm-supports-reasoning"
                type="checkbox"
                checked={supportsReasoning}
                onChange={(e) => setSupportsReasoning(e.target.checked)}
              />
              <Label htmlFor="llm-supports-reasoning">
                {t("pages.llmModels.form.supportsReasoning")}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("pages.llmModels.form.supportsReasoningHint")}
            </p>
            {supportsReasoning ? (
              <div className="mt-2 space-y-1">
                <Label htmlFor="llm-default-effort">
                  {t("pages.llmModels.form.defaultEffort")}
                </Label>
                <Select
                  value={defaultEffort ?? "__none__"}
                  onValueChange={(v) =>
                    setDefaultEffort(
                      v === "__none__" ? null : (v as ReasoningEffort),
                    )
                  }
                >
                  <SelectTrigger id="llm-default-effort">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      {t("pages.llmModels.form.defaultEffortNone")}
                    </SelectItem>
                    <SelectItem value="minimal">
                      {t("pages.llmModels.form.effort.minimal")}
                    </SelectItem>
                    <SelectItem value="low">
                      {t("pages.llmModels.form.effort.low")}
                    </SelectItem>
                    <SelectItem value="medium">
                      {t("pages.llmModels.form.effort.medium")}
                    </SelectItem>
                    <SelectItem value="high">
                      {t("pages.llmModels.form.effort.high")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("actions.cancel")}
            </Button>
            <Button type="submit" disabled={disabled}>
              {isEdit ? t("actions.save") : t("actions.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
